//changed due to package upgrade
import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useNavigate } from 'react-router-dom';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import jwtAxios from '@projectK/services/auth/JWT';
import { isTournamentCoach } from '@projectK/context/RolesProvider';
import AppsPagination from '@projectK/components/AppsPagination';
import AppLoader from '@projectK/components/AppLoader';
import {
  formatStartDate,
  timeZoneDate,
} from '../../../../../../../libs/modules/src/lib/thirdParty/DateFormat';
import IntlMessages from '@projectK/helpers/IntlMessages';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { LuUsersRound } from 'react-icons/lu';
import { GrTrophy } from 'react-icons/gr';
import { RxLightningBolt } from 'react-icons/rx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

const quickActions = [
  {
    title: (
      <IntlMessages id='headCoachdashboard.quickActions.viewManageTeams.title' />
    ),
    subtitle: (
      <IntlMessages id='headCoachdashboard.quickActions.viewManageTeams.subtitle' />
    ),
    icon: <LuUsersRound fontSize='large' />,
    bgcolor: '#3c83f6',
    color: '#fff',
    link: '/coach/teams',
  },
  {
    title: (
      <IntlMessages id='headCoachdashboard.quickActions.filterTournaments.title' />
    ),
    subtitle: (
      <IntlMessages id='headCoachdashboard.quickActions.filterTournaments.subtitle' />
    ),
    icon: <SearchIcon fontSize='large' />,
    bgcolor: '#7c3bed',
    color: '#fff',
    link: '/tournaments',
  },
  {
    title: (
      <IntlMessages id='headCoachdashboard.quickActions.exportTeamData.title' />
    ),
    subtitle: (
      <IntlMessages id='headCoachdashboard.quickActions.exportTeamData.subtitle' />
    ),
    icon: <FileDownloadOutlinedIcon fontSize='large' />,
    bgcolor: '#f97415',
    color: '#fff',
    link: '/coach/teams',
  },
  {
    title: (
      <IntlMessages id='headCoachdashboard.quickActions.tournamentRegistration.title' />
    ),
    subtitle: (
      <IntlMessages id='headCoachdashboard.quickActions.tournamentRegistration.subtitle' />
    ),
    icon: <GrTrophy fontSize='large' />,
    bgcolor: '#2a9d90',
    color: '#fff',
    link: '/tournaments',
  },
  {
    title: (
      <IntlMessages id='headCoachdashboard.quickActions.transactionHistory.title' />
    ),
    subtitle: (
      <IntlMessages id='headCoachdashboard.quickActions.transactionHistory.subtitle' />
    ),
    icon: <NotificationsNoneOutlinedIcon fontSize='large' />,
    bgcolor: '#6467f2',
    color: '#fff',
    link: '/transaction-history',
  },
];

const HeadCoachDashboard = () => {
  const navigate = useNavigate();
  const todayDate = timeZoneDate().todayDate;
  const [page, setPage] = useState(0);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [perPage] = useState(5);
  const [totalTournamentCount, setTotalTournamentCount] = useState(0);
  const [upcomingTournamentCount, setUpcomingTournamentCount] = useState(0);
  const onPageChange = (event, value) => {
    setPage(value);
  };
  const { user } = useAuthUser();

  const fetchData = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (isTournamentCoach(user)) {
        filters.user_id = user.id;
      }
      const response = await jwtAxios.get(`/teams`, {
        params: {
          page,
          perPage,
          filters,
        },
      });
      const { data } = response;
      setList(data?.list);
      setTotal(data?.total);
      setTotalTournamentCount(data?.totalTournaments);
      data.list.length === 0 && page > 0 && setPage(page - 1);
      setLoading(false);
    } catch (error) {
      console.error('An error occurred:', error);
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
    fetchDashboardData();
  }, [page, perPage]);

  const fetchDashboardData = async () => {
    try {
      const response = await jwtAxios.get(
        `/dashboard/dashboard/tournaments-count`,
      );
      const { data } = response;
      setTotalTournamentCount(data?.totalTournaments);
      setUpcomingTournamentCount(data?.upcomingTournaments);
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };
  const stats = [
    {
      title: <IntlMessages id='headCoachdashboard.teamsRegistered.title' />,
      value: total,
      sub1: `Across ${totalTournamentCount || 0} tournaments`,
      bgcolor: '#dbeafe',
      color: '#2362eb',
      subcolor: '#2362eb',
      icon: <GroupsIcon fontSize='large' />,
    },
    {
      title: <IntlMessages id='headCoachdashboard.upcomingTournaments.title' />,
      value: upcomingTournamentCount,
      sub1: <IntlMessages id='headCoachdashboard.upcomingTournaments.sub1' />,
      bgcolor: '#dcfce7',
      color: '#12a146',
      subcolor: '#12a146',
      icon: <CalendarTodayOutlinedIcon fontSize='large' />,
    },
    {
      title: <IntlMessages id='headCoachdashboard.teamCoaches.title' />,
      value: total,
      sub1: <IntlMessages id='headCoachdashboard.teamCoaches.sub1' />,
      bgcolor: '#feede1',
      color: '#f97415',
      subcolor: '#f97415',
      icon: <EmojiEventsIcon fontSize='large' />,
    },
  ];
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationData, setNotificationData] = useState([]);
  const fetchNotifications = async () => {
    try {
      setNotificationLoading(true);
      const response = await jwtAxios.get(`/notification`);
      const { data } = response;
      setNotificationData(data.list);
      setNotificationLoading(false);
    } catch (error) {
      console.error('Error fetching notifications', error);
      setNotificationLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Inside HeadCoachDashboard component
  const renderNotifications = () => {
    if (notificationLoading) {
      return (
        <Box display='flex' justifyContent='center' py={5}>
          <CircularProgress size={30} thickness={4} sx={{ color: '#3c83f6' }} />
        </Box>
      );
    }

    if (notificationData?.length > 0) {
      return notificationData.map((notification, index) => (
        <Card
          key={index + 1}
          sx={{
            marginBottom: '8px',
            border: notification.urgent ? '1px solid #ef5350' : '#eee',
          }}
        >
          <CardContent>
            <Typography variant='body2' sx={{ mb: 1 }} color='text.primary'>
              {notification?.notification_message}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {dayjs(notification.created_at).fromNow()}
            </Typography>
          </CardContent>
        </Card>
      ));
    }

    return (
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ textAlign: 'center', py: 3, fontWeight: '600' }}
      >
        No new notifications.
      </Typography>
    );
  };

  return (
    <Box className='admin-dashboard'>
      {/* Header */}
      <Box
        className='headerbg'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
      >
        <Box>
          <Typography variant='h1'>
            Welcome {user?.first_name} {user?.last_name}
          </Typography>
          <Typography sx={{ color: '#eee', mb: 3 }}>
            <IntlMessages id='headCoachdasboard.title' />
          </Typography>
        </Box>
        <Box>
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography variant='h2'>
              {' '}
              <IntlMessages id='common.today' />
            </Typography>
            <Typography>{todayDate.toFormat('MMMM dd, yyyy')}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Stats Section */}
      <Grid container spacing={{ xs: 2, md: 5 }} className='stats'>
        {stats.map((item, index) => (
          <Grid size={{ xs: 6, sm: 6, md: 4 }} key={index + 1}>
            <Card sx={{ border: 1, borderColor: item.color }}>
              <CardContent>
                {/* Header row: title + icon */}
                <Box className='title_icon'>
                  <Typography variant='subtitle2'>{item.title}</Typography>
                  <Box sx={{ bgcolor: item.bgcolor, color: item.color }}>
                    {item.icon}
                  </Box>
                </Box>

                <Typography
                  variant='h4'
                  sx={{ fontWeight: 'bold', mt: 1, color: '#000' }}
                >
                  {item.value}
                </Typography>
                <Typography
                  className='sub_item1'
                  variant='subtitle1'
                  color='text.secondary'
                >
                  {item.sub1}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box className='quick-actions'>
        <Typography
          variant='h2'
          sx={{
            fontWeight: 'bold',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <RxLightningBolt fontSize='23' color='#0A8FDC' />
          <IntlMessages id='superAdmin.quickactions.quickActions' />
        </Typography>
        <Grid container spacing={{ xs: 2, md: 5 }}>
          {quickActions.map((action, index) => (
            <Grid size={{ xs: 6, sm: 6, md: 2.4 }} key={index + 1}>
              <Card
                sx={{
                  boxShadow: 2,
                  '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                  transition: '0.3s',
                }}
                onClick={() => navigate(action.link)}
              >
                <Box
                  sx={{
                    bgcolor: action.bgcolor,
                    color: action.color,
                  }}
                >
                  {action.icon}
                </Box>
                <Typography variant='h3' fontWeight='500'>
                  {action.title}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {action.subtitle}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Recent Activity & Updates */}
      <Grid
        container
        spacing={{ xs: 2, md: 5 }}
        className='activity-updates'
        mb={5}
      >
        <Grid size={{ xs: 12, md: 8 }}>
          <Box className='my-teams-section'>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              mb={2}
            >
              <Typography
                variant='h2'
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <PeopleAltOutlinedIcon fontSize='large' color='primary' />
                <IntlMessages id='common.myTeams' />
              </Typography>
            </Box>
            <Box
              className='my-teams-cards'
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              {loading && (
                <Box display='flex' justifyContent='center' py={5}>
                  <AppLoader />
                </Box>
              )}

              {!loading &&
                list?.length > 0 &&
                list.map((team, index) => (
                  <Card
                    key={index + 1}
                    sx={{
                      p: 1,
                      '&:hover': { bgcolor: '#f5f5f5' },
                    }}
                  >
                    <CardContent>
                      <Typography variant='h3' fontWeight='500'>
                        {team?.team_name}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {team?.division_name}
                      </Typography>
                      <Box
                        className='date_location'
                        display='flex'
                        alignItems='center'
                        gap={3}
                        mt={1}
                        sx={{ flexWrap: 'wrap' }}
                      >
                        <Box display='flex' alignItems='center' gap={0.5}>
                          <CalendarTodayOutlinedIcon
                            sx={{ fontSize: '1rem', color: 'grey' }}
                          />
                          <Typography variant='body2'>
                            {formatStartDate(team?.start_date).date}
                          </Typography>
                        </Box>
                        <Box display='flex' alignItems='center' gap={0.5}>
                          <LocationOnIcon
                            sx={{ fontSize: '1rem', color: 'grey' }}
                          />
                          <Typography variant='body2'>
                            {team?.location_name} - {team?.address}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}

              {!loading && list?.length === 0 && (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  fontWeight='600'
                >
                  No teams found.
                </Typography>
              )}
            </Box>

            <Box
              className='pagination'
              display='flex'
              justifyContent='flex-end'
              alignItems='center'
              mt={2}
              gap={1}
            >
              <AppsPagination
                className='cols-pagination'
                rowsPerPage={perPage}
                count={total}
                page={page}
                onPageChange={onPageChange}
              />
            </Box>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Box mb={3} className='updates_notifications'>
            <Typography
              variant='h2'
              sx={{
                fontWeight: 'bold',
                mb: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CampaignOutlinedIcon fontSize='large' color='primary' />
              Tournament Updates & Notifications
            </Typography>
            <Box className='notification_boxes'>{renderNotifications()}</Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HeadCoachDashboard;
